import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  Lead,
  Contact,
  LeadActivity,
  BackendInteraction,
  Booking,
  LeadStatus,
  LeadScore,
} from '@/types'
import { api, isApiError } from '@/api/client'
import { createRequestSequencer } from './utils/requestSequencer'
import { LEAD_ACTIVITY_TYPES } from '@/types/leadActivity'
import type { KeyPerson, LeadOutreach } from '@/types/prospecting'

// Backend lead shape (snake_case, numeric score, split name)
interface BackendLead {
  id: string
  business_id: string
  status: LeadStatus
  source: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  company: string | null
  notes: string | null
  score: number
  tags: string[]
  custom_fields: Record<string, unknown>
  converted_at: string | null
  created_at: string
  updated_at: string
}

// Map numeric score to LeadScore label
function numericScoreToLabel(score: number): LeadScore {
  if (score >= 70) return 'hot'
  if (score >= 40) return 'warm'
  return 'cold'
}

// Map a backend lead response to the frontend Lead type
function mapBackendLead(raw: BackendLead): Lead {
  const firstName = raw.first_name ?? ''
  const lastName = raw.last_name ?? ''
  // A business lead (e.g. one Outreach found) may have no contact person yet.
  const contactName = [firstName, lastName].filter(Boolean).join(' ')
  const name = contactName || raw.company || raw.email || 'Unknown'

  return {
    id: raw.id,
    name,
    contactName,
    email: raw.email ?? '',
    phone: raw.phone ?? undefined,
    company: raw.company ?? undefined,
    source: raw.source ?? '',
    status: raw.status,
    score: numericScoreToLabel(raw.score),
    scoreValue: raw.score,
    notes: raw.notes ?? undefined,
    tags: raw.tags,
    outreach: leadOutreach(raw),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

// What Outreach keeps on a lead it found (custom_fields written by prospecting/crm.py).
function leadOutreach(raw: BackendLead): LeadOutreach | undefined {
  const fields = raw.custom_fields ?? {}
  const prospectId = fields.prospect_id
  if (raw.source !== 'outreach' || typeof prospectId !== 'string') return undefined
  return {
    prospectId,
    about: typeof fields.about === 'string' ? fields.about : null,
    people: Array.isArray(fields.people) ? (fields.people as KeyPerson[]) : [],
    website: typeof fields.website === 'string' ? fields.website : null,
    socials: (fields.socials as Record<string, string> | undefined) ?? {},
  }
}

// Map frontend Lead fields to backend create/update payload
function mapFrontendLeadToBackend(
  data: Partial<Lead> & { name?: string }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  if (data.name !== undefined) {
    const parts = data.name.trim().split(/\s+/)
    payload.first_name = parts[0] || ''
    payload.last_name = parts.slice(1).join(' ') || ''
  }
  if (data.email !== undefined) payload.email = data.email
  if (data.phone !== undefined) payload.phone = data.phone
  if (data.company !== undefined) payload.company = data.company
  if (data.source !== undefined) payload.source = data.source
  if (data.status !== undefined) payload.status = data.status
  if (data.notes !== undefined) payload.notes = data.notes
  if (data.tags !== undefined) payload.tags = data.tags

  return payload
}

// Unwrap API response: real backend returns T directly, mocks return {data: T}
function unwrapData<T>(response: { data: T } | T): T {
  const r = response as Record<string, unknown>
  if (r && typeof r === 'object' && 'data' in r && r.data && typeof r.data === 'object') {
    return r.data as T
  }
  return response as T
}

// Map a backend interaction row (snake_case) to the frontend LeadActivity type
function mapBackendInteraction(raw: BackendInteraction): LeadActivity {
  const type = LEAD_ACTIVITY_TYPES.includes(raw.type as LeadActivity['type'])
    ? (raw.type as LeadActivity['type'])
    : 'note'
  const createdBy = raw.metadata?.created_by
  return {
    id: raw.id,
    leadId: raw.lead_id ?? '',
    type,
    description: raw.summary ?? '',
    createdAt: raw.created_at,
    createdBy: typeof createdBy === 'string' ? createdBy : 'System',
  }
}

// Backend contact shape (snake_case from Python API)
interface BackendContact {
  id: string
  business_id: string
  lead_id: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  company: string | null
  lifetime_value: number
  last_interaction_at: string | null
  tags: string[]
  custom_fields: Record<string, unknown>
  created_at: string
  updated_at: string
}

// Backend booking shape (snake_case from Python API)
interface BackendBooking {
  id: string
  business_id: string
  lead_id: string | null
  contact_id: string | null
  external_id: string | null
  source: string | null
  status: string
  title: string | null
  start_time: string | null
  end_time: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

// Map a backend contact response to the frontend Contact type
function mapBackendContact(raw: BackendContact): Contact {
  const firstName = raw.first_name ?? ''
  const lastName = raw.last_name ?? ''
  const name = [firstName, lastName].filter(Boolean).join(' ') || raw.email || 'Unknown'
  const type: Contact['type'] = raw.lead_id ? 'lead' : 'customer'

  return {
    id: raw.id,
    name,
    email: raw.email ?? '',
    phone: raw.phone ?? undefined,
    company: raw.company ?? undefined,
    type,
    lastContactAt: raw.last_interaction_at ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

// Map frontend Contact fields to backend create/update payload
function mapFrontendContactToBackend(
  data: Partial<Contact> & { name?: string }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  if (data.name !== undefined) {
    const parts = data.name.trim().split(/\s+/)
    payload.first_name = parts[0] || ''
    payload.last_name = parts.slice(1).join(' ') || ''
  }
  if (data.email !== undefined) payload.email = data.email
  if (data.phone !== undefined) payload.phone = data.phone
  if (data.company !== undefined) payload.company = data.company

  return payload
}

// Map a backend booking response to the frontend Booking type
function mapBackendBooking(raw: BackendBooking): Booking {
  return {
    id: raw.id,
    contactId: raw.contact_id || raw.lead_id || '',
    contactName: raw.title || '',
    type: (raw.source as Booking['type']) || 'meeting',
    startTime: raw.start_time || '',
    endTime: raw.end_time || '',
    notes: raw.notes ?? undefined,
    status: (raw.status as Booking['status']) || 'scheduled',
  }
}

// Map frontend Booking fields to backend create/update payload
function mapFrontendBookingToBackend(data: Partial<Booking>): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  if (data.contactId !== undefined) payload.contact_id = data.contactId
  if (data.contactName !== undefined) payload.title = data.contactName
  if (data.type !== undefined) payload.source = data.type
  if (data.startTime !== undefined) payload.start_time = data.startTime
  if (data.endTime !== undefined) payload.end_time = data.endTime
  if (data.notes !== undefined) payload.notes = data.notes
  if (data.status !== undefined) payload.status = data.status

  return payload
}

// Lead filters
export interface LeadFilters {
  status?: string // comma-separated stored statuses (one stage)
  score?: LeadScore
  search?: string
  source?: string
  outreach_campaign_id?: string
}

// CRM store state interface
interface CrmState {
  // Leads
  leads: Lead[]
  selectedLead: Lead | null
  leadsLoading: boolean
  leadsError: string | null
  leadsPagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  leadsFilters: LeadFilters

  // Lead activities
  activities: LeadActivity[]
  activitiesLoading: boolean
  activitiesError: string | null

  // Contacts
  contacts: Contact[]
  selectedContact: Contact | null
  contactsLoading: boolean
  contactsError: string | null
  contactsPagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }

  // Bookings
  bookings: Booking[]
  bookingsLoading: boolean
  bookingsError: string | null
}

// CRM store actions interface
interface CrmActions {
  // Lead actions
  fetchLeads: (page?: number, pageSize?: number) => Promise<void>
  fetchLeadById: (id: string) => Promise<void>
  createLead: (lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Lead>
  updateLead: (id: string, updates: Partial<Lead>) => Promise<void>
  deleteLead: (id: string) => Promise<void>
  setSelectedLead: (lead: Lead | null) => void
  setLeadsFilters: (filters: LeadFilters) => void
  clearLeadsFilters: () => void
  updateLeadStatus: (id: string, status: LeadStatus) => Promise<void>
  updateLeadStatusOptimistic: (id: string, status: LeadStatus) => Promise<void>
  updateLeadNotesOptimistic: (id: string, notes: string) => Promise<void>

  // Lead activity actions
  fetchLeadActivities: (leadId: string) => Promise<void>
  addLeadActivity: (activity: Omit<LeadActivity, 'id' | 'createdAt'>) => Promise<LeadActivity>

  // Contact actions
  fetchContacts: (page?: number, pageSize?: number) => Promise<void>
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Contact>
  updateContact: (id: string, updates: Partial<Contact>) => Promise<void>
  deleteContact: (id: string) => Promise<void>
  setSelectedContact: (contact: Contact | null) => void

  // Booking actions
  fetchBookings: () => Promise<void>
  createBooking: (booking: Omit<Booking, 'id'>) => Promise<Booking>
  updateBooking: (id: string, updates: Partial<Booking>) => Promise<void>
  deleteBooking: (id: string) => Promise<void>

  // Error clearing
  clearErrors: () => void
}

// Combined store type
type CrmStore = CrmState & CrmActions

// Initial state
const initialState: CrmState = {
  leads: [],
  selectedLead: null,
  leadsLoading: false,
  leadsError: null,
  leadsPagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  leadsFilters: {},

  activities: [],
  activitiesLoading: false,
  activitiesError: null,

  contacts: [],
  selectedContact: null,
  contactsLoading: false,
  contactsError: null,
  contactsPagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },

  bookings: [],
  bookingsLoading: false,
  bookingsError: null,
}

// Sequences overlapping fetchLeads calls so a stale (out-of-order) resolution
// can never leave a stuck error banner over a freshly loaded board (issue #1313).
const leadsFetchSeq = createRequestSequencer()

// Create the CRM store with devtools middleware
export const useCrmStore = create<CrmStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Lead actions
      fetchLeads: async (page = 1, pageSize = 10) => {
        const token = leadsFetchSeq.begin()
        set({ leadsLoading: true, leadsError: null })
        try {
          const { leadsFilters } = get()
          const response = await api.getList<BackendLead>('/crm/leads', {
            params: {
              limit: pageSize,
              offset: (page - 1) * pageSize,
              ...leadsFilters,
            },
          })
          // A newer fetchLeads superseded this one — discard the result so a
          // slow success can't clobber fresher state.
          if (!leadsFetchSeq.isCurrent(token)) return
          set({
            leads: (response.data ?? []).map(mapBackendLead),
            leadsPagination: {
              page: response.page,
              pageSize: response.pageSize,
              total: response.total,
              totalPages: response.totalPages,
            },
            leadsLoading: false,
            leadsError: null,
          })
        } catch (error) {
          // A newer fetchLeads superseded this one — never surface a stale error
          // over a board another request already loaded successfully.
          if (!leadsFetchSeq.isCurrent(token)) return
          const message = isApiError(error) ? error.message : 'Failed to fetch leads'
          set({ leadsError: message, leadsLoading: false })
        }
      },

      fetchLeadById: async (id: string) => {
        set({ leadsLoading: true, leadsError: null })
        try {
          const response = await api.get<BackendLead>(`/crm/leads/${id}`)
          set({ selectedLead: mapBackendLead(unwrapData(response)), leadsLoading: false })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to fetch lead'
          set({ leadsError: message, leadsLoading: false })
        }
      },

      createLead: async (lead) => {
        set({ leadsLoading: true, leadsError: null })
        try {
          const backendPayload = mapFrontendLeadToBackend(lead)
          const response = await api.post<BackendLead>('/crm/leads', backendPayload)
          const newLead = mapBackendLead(unwrapData(response))
          set((state) => ({
            leads: [newLead, ...state.leads],
            leadsLoading: false,
          }))
          return newLead
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to create lead'
          set({ leadsError: message, leadsLoading: false })
          throw error
        }
      },

      updateLead: async (id: string, updates: Partial<Lead>) => {
        set({ leadsLoading: true, leadsError: null })
        try {
          const backendPayload = mapFrontendLeadToBackend(updates)
          const response = await api.patch<BackendLead>(`/crm/leads/${id}`, backendPayload)
          const updatedLead = mapBackendLead(unwrapData(response))
          set((state) => ({
            leads: state.leads.map((lead) => (lead.id === id ? updatedLead : lead)),
            selectedLead: state.selectedLead?.id === id ? updatedLead : state.selectedLead,
            leadsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update lead'
          set({ leadsError: message, leadsLoading: false })
          throw error
        }
      },

      deleteLead: async (id: string) => {
        set({ leadsLoading: true, leadsError: null })
        try {
          await api.delete(`/crm/leads/${id}`)
          set((state) => ({
            leads: state.leads.filter((lead) => lead.id !== id),
            selectedLead: state.selectedLead?.id === id ? null : state.selectedLead,
            leadsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to delete lead'
          set({ leadsError: message, leadsLoading: false })
          throw error
        }
      },

      setSelectedLead: (lead: Lead | null) => {
        set({ selectedLead: lead })
      },

      setLeadsFilters: (filters: LeadFilters) => {
        set({ leadsFilters: filters })
      },

      clearLeadsFilters: () => {
        set({ leadsFilters: {} })
      },

      updateLeadStatus: async (id: string, status: LeadStatus) => {
        await get().updateLead(id, { status })
      },

      // Kanban drag: apply the status synchronously (no leadsLoading gate),
      // PATCH with manual=true so the backend accepts any-column-to-any-column
      // human moves, and roll back on failure. Mirrors useDraftsStore's
      // updateDraftStatus.
      updateLeadStatusOptimistic: async (id: string, status: LeadStatus) => {
        const { leads } = get()
        const lead = leads.find((l) => l.id === id)
        if (!lead) return

        const oldStatus = lead.status

        set({
          leads: leads.map((l) => (l.id === id ? { ...l, status } : l)),
        })

        try {
          const response = await api.patch<BackendLead>(`/crm/leads/${id}`, {
            status,
            manual: true,
          })
          const updatedLead = mapBackendLead(unwrapData(response))
          set((state) => ({
            leads: state.leads.map((l) => (l.id === id ? updatedLead : l)),
            selectedLead: state.selectedLead?.id === id ? updatedLead : state.selectedLead,
            // A successful drag clears any prior stale error so the banner
            // can't outlive the failure that set it (issue #1313).
            leadsError: null,
          }))
        } catch (error) {
          set((state) => ({
            leads: state.leads.map((l) => (l.id === id ? { ...l, status: oldStatus } : l)),
            leadsError: isApiError(error) ? error.message : 'Failed to update lead status',
          }))
          throw error
        }
      },

      // Triage note save: apply the merged notes synchronously (no
      // leadsLoading gate — triage taps must never wait on a spinner),
      // PATCH the flat `notes` column, and roll back on failure. The
      // caller supplies the already-merged notes string (see
      // appendTimestampedNote in leadTriageQueue.ts).
      updateLeadNotesOptimistic: async (id: string, notes: string) => {
        const { leads } = get()
        const lead = leads.find((l) => l.id === id)
        if (!lead) return

        const oldNotes = lead.notes

        set({
          leads: leads.map((l) => (l.id === id ? { ...l, notes } : l)),
        })

        try {
          const response = await api.patch<BackendLead>(`/crm/leads/${id}`, { notes })
          const updatedLead = mapBackendLead(unwrapData(response))
          set((state) => ({
            leads: state.leads.map((l) => (l.id === id ? updatedLead : l)),
            selectedLead: state.selectedLead?.id === id ? updatedLead : state.selectedLead,
          }))
        } catch (error) {
          set((state) => ({
            leads: state.leads.map((l) => (l.id === id ? { ...l, notes: oldNotes } : l)),
            leadsError: isApiError(error) ? error.message : 'Failed to save note',
          }))
          throw error
        }
      },

      // Lead activity actions
      fetchLeadActivities: async (leadId: string) => {
        set({ activitiesLoading: true, activitiesError: null })
        try {
          const response = await api.getList<BackendInteraction>(`/crm/leads/${leadId}/activities`)
          set({
            activities: (response.data ?? []).map(mapBackendInteraction),
            activitiesLoading: false,
          })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to fetch activities'
          set({ activitiesError: message, activitiesLoading: false })
        }
      },

      addLeadActivity: async (activity) => {
        set({ activitiesLoading: true, activitiesError: null })
        try {
          const response = await api.post<BackendInteraction>(
            `/crm/leads/${activity.leadId}/activities`,
            {
              type: activity.type,
              summary: activity.description,
              metadata: { created_by: activity.createdBy },
            }
          )
          const newActivity = mapBackendInteraction(unwrapData(response))
          set((state) => ({
            activities: [newActivity, ...state.activities],
            activitiesLoading: false,
          }))
          return newActivity
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to add activity'
          set({ activitiesError: message, activitiesLoading: false })
          throw error
        }
      },

      // Contact actions
      fetchContacts: async (page = 1, pageSize = 10) => {
        set({ contactsLoading: true, contactsError: null })
        try {
          const response = await api.getList<BackendContact>('/crm/contacts', {
            params: {
              limit: pageSize,
              offset: (page - 1) * pageSize,
            },
          })
          set({
            contacts: (response.data ?? []).map(mapBackendContact),
            contactsPagination: {
              page: response.page,
              pageSize: response.pageSize,
              total: response.total,
              totalPages: response.totalPages,
            },
            contactsLoading: false,
          })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to fetch contacts'
          set({ contactsError: message, contactsLoading: false })
        }
      },

      createContact: async (contact) => {
        set({ contactsLoading: true, contactsError: null })
        try {
          const backendPayload = mapFrontendContactToBackend(contact)
          const response = await api.post<BackendContact>('/crm/contacts', backendPayload)
          const newContact = mapBackendContact(unwrapData(response))
          set((state) => ({
            contacts: [newContact, ...state.contacts],
            contactsLoading: false,
          }))
          return newContact
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to create contact'
          set({ contactsError: message, contactsLoading: false })
          throw error
        }
      },

      updateContact: async (id: string, updates: Partial<Contact>) => {
        set({ contactsLoading: true, contactsError: null })
        try {
          const backendPayload = mapFrontendContactToBackend(updates)
          const response = await api.patch<BackendContact>(`/crm/contacts/${id}`, backendPayload)
          const updatedContact = mapBackendContact(unwrapData(response))
          set((state) => ({
            contacts: state.contacts.map((contact) =>
              contact.id === id ? updatedContact : contact
            ),
            selectedContact:
              state.selectedContact?.id === id ? updatedContact : state.selectedContact,
            contactsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update contact'
          set({ contactsError: message, contactsLoading: false })
          throw error
        }
      },

      deleteContact: async (id: string) => {
        set({ contactsLoading: true, contactsError: null })
        try {
          await api.delete(`/crm/contacts/${id}`)
          set((state) => ({
            contacts: state.contacts.filter((contact) => contact.id !== id),
            contactsPagination: {
              ...state.contactsPagination,
              total: Math.max(0, state.contactsPagination.total - 1),
            },
            selectedContact: state.selectedContact?.id === id ? null : state.selectedContact,
            contactsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to delete contact'
          set({ contactsError: message, contactsLoading: false })
          throw error
        }
      },

      setSelectedContact: (contact: Contact | null) => {
        set({ selectedContact: contact })
      },

      // Booking actions
      fetchBookings: async () => {
        set({ bookingsLoading: true, bookingsError: null })
        try {
          const response = await api.getList<BackendBooking>('/crm/bookings')
          set({ bookings: (response.data ?? []).map(mapBackendBooking), bookingsLoading: false })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to fetch bookings'
          set({ bookingsError: message, bookingsLoading: false })
        }
      },

      createBooking: async (booking) => {
        set({ bookingsLoading: true, bookingsError: null })
        try {
          const backendPayload = mapFrontendBookingToBackend(booking)
          const response = await api.post<BackendBooking>('/crm/bookings', backendPayload)
          const newBooking = mapBackendBooking(unwrapData(response))
          set((state) => ({
            bookings: [...state.bookings, newBooking],
            bookingsLoading: false,
          }))
          return newBooking
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to create booking'
          set({ bookingsError: message, bookingsLoading: false })
          throw error
        }
      },

      updateBooking: async (id: string, updates: Partial<Booking>) => {
        set({ bookingsLoading: true, bookingsError: null })
        try {
          const backendPayload = mapFrontendBookingToBackend(updates)
          const response = await api.patch<BackendBooking>(`/crm/bookings/${id}`, backendPayload)
          const updatedBooking = mapBackendBooking(unwrapData(response))
          set((state) => ({
            bookings: state.bookings.map((booking) =>
              booking.id === id ? updatedBooking : booking
            ),
            bookingsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update booking'
          set({ bookingsError: message, bookingsLoading: false })
          throw error
        }
      },

      deleteBooking: async (id: string) => {
        set({ bookingsLoading: true, bookingsError: null })
        try {
          await api.delete(`/crm/bookings/${id}`)
          set((state) => ({
            bookings: state.bookings.filter((booking) => booking.id !== id),
            bookingsLoading: false,
          }))
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to delete booking'
          set({ bookingsError: message, bookingsLoading: false })
          throw error
        }
      },

      // Error clearing
      clearErrors: () => {
        set({
          leadsError: null,
          activitiesError: null,
          contactsError: null,
          bookingsError: null,
        })
      },
    }),
    { name: 'CrmStore', enabled: import.meta.env.DEV }
  )
)

// Selectors for common access patterns
export const selectLeads = (state: CrmStore) => state.leads
export const selectSelectedLead = (state: CrmStore) => state.selectedLead
export const selectLeadsLoading = (state: CrmStore) => state.leadsLoading
export const selectLeadsError = (state: CrmStore) => state.leadsError
export const selectLeadsPagination = (state: CrmStore) => state.leadsPagination
export const selectLeadsFilters = (state: CrmStore) => state.leadsFilters

export const selectActivities = (state: CrmStore) => state.activities
export const selectActivitiesLoading = (state: CrmStore) => state.activitiesLoading
export const selectActivitiesError = (state: CrmStore) => state.activitiesError

export const selectContacts = (state: CrmStore) => state.contacts
export const selectSelectedContact = (state: CrmStore) => state.selectedContact
export const selectContactsLoading = (state: CrmStore) => state.contactsLoading
export const selectContactsError = (state: CrmStore) => state.contactsError
export const selectContactsPagination = (state: CrmStore) => state.contactsPagination

export const selectBookings = (state: CrmStore) => state.bookings
export const selectBookingsLoading = (state: CrmStore) => state.bookingsLoading
export const selectBookingsError = (state: CrmStore) => state.bookingsError

// Filtered selectors
export const selectLeadsByStatus = (status: LeadStatus) => (state: CrmStore) =>
  state.leads.filter((lead) => lead.status === status)

export const selectLeadsByScore = (score: LeadScore) => (state: CrmStore) =>
  state.leads.filter((lead) => lead.score === score)

export const selectHotLeads = (state: CrmStore) =>
  state.leads.filter((lead) => lead.score === 'hot')

export const selectUpcomingBookings = (state: CrmStore) =>
  state.bookings
    .filter((booking) => booking.status === 'scheduled' && new Date(booking.startTime) > new Date())
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
